import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  NavigationError,
  provideRouter,
  withComponentInputBinding,
  withNavigationErrorHandler,
} from '@angular/router';
import { ErrorHandler } from '@angular/core';
import {
  inject,
  ApplicationConfig,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { includeBearerTokenInterceptor } from 'keycloak-angular';
import { provideKeycloakAngular } from '../configs/keycloak.config';
import { registerLicense } from '@syncfusion/ej2-base';
import { ColumnChooserService } from '@syncfusion/ej2-angular-grids';
import { routes } from './app.routes';
import {
  ClientDiagnosticsService,
  correlationIdInterceptor,
  I18N,
  languageInterceptor,
  provideClientDiagnostics,
} from 'O3_core';
import * as Sentry from '@sentry/angular';
import { Router } from '@angular/router';
import { DiagnosticsErrorHandler } from './diagnostics-error-handler';
import { HealthCheckService } from '../layout/health-check.service';

registerLicense(
  'Ngo9BigBOggjHTQxAR8/V1JEaF5cXmRCf1FpRmJGdld5fUVHYVZUTXxaS00DNHVRdkdmWXZecXRQRWNcUkB2XUVWYE0='
);

const CHUNK_RELOAD_TS_KEY = 'o3-chunk-error-reload-ts';
const CHUNK_RELOAD_MIN_INTERVAL_MS = 10_000;

// Chrome, Safari and Firefox each word the failed dynamic-import error differently.
const STALE_CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;

function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return STALE_CHUNK_ERROR_PATTERN.test(message);
}

/**
 * After a deployment, `outputHashing: all` renames every lazy chunk and the old
 * files are gone from the server, so tabs opened before the deploy fail to
 * lazy-load routes. A full reload of the target URL fetches the fresh
 * index.html and chunk manifest. Rate-limited via sessionStorage so a chunk
 * that is genuinely unavailable cannot cause a reload loop.
 */
function reloadOnStaleChunk(navigationError: NavigationError): void {
  if (!isStaleChunkError(navigationError.error)) {
    return;
  }

  const lastReloadTs = Number(sessionStorage.getItem(CHUNK_RELOAD_TS_KEY) ?? '0');
  if (Date.now() - lastReloadTs < CHUNK_RELOAD_MIN_INTERVAL_MS) {
    return;
  }

  sessionStorage.setItem(CHUNK_RELOAD_TS_KEY, String(Date.now()));
  window.location.assign(navigationError.url);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withComponentInputBinding(), withNavigationErrorHandler(reloadOnStaleChunk)),
    provideHttpClient(
      withInterceptors([correlationIdInterceptor, languageInterceptor, includeBearerTokenInterceptor])
    ),
    provideClientDiagnostics(),
    ColumnChooserService,
    provideKeycloakAngular(),
    provideAppInitializer(() => {
      const i18n = inject(I18N);
      i18n.init();
      return;
    }),
    provideAppInitializer(() => {
      // Stamp the running build onto every ticket. Fire-and-forget: the ops
      // health check must never gate app startup, so a failure here just
      // leaves `appVersion` undefined in the ticket context.
      const diagnostics = inject(ClientDiagnosticsService);
      inject(HealthCheckService)
        .getHealthCheck()
        .subscribe({
          next: (health) =>
            diagnostics.setAppVersion(health.version, health.releasedAt),
          error: () => undefined,
        });
    }),
    {
      // Records into ClientDiagnosticsService, then delegates to Sentry —
      // Sentry behaviour is unchanged, see DiagnosticsErrorHandler.
      provide: ErrorHandler,
      useClass: DiagnosticsErrorHandler,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      // Initialize Sentry TraceService for routing instrumentation
      inject(Sentry.TraceService);
    }),
  ],
};
